(() => {
  'use strict';

  if (window.PensualizeiProductCardV2) return;
  window.PensualizeiProductCardV2 = true;

  const SELECTORS = {
    card: '[data-ps-card]',
    action: '[data-ps-card-action]',
    dialog: '[data-ps-variant-dialog]',
    close: '[data-ps-dialog-close]',
    form: '[data-ps-variant-form]',
    group: '[data-ps-option-group]',
    value: '[data-ps-option-value]',
    variants: '[data-ps-variants]',
    variantInput: '[data-ps-variant-id]',
    status: '[data-ps-selection-status]',
    error: '[data-ps-form-error]',
    submit: '[data-ps-panel-submit]',
    submitLabel: '[data-ps-panel-submit-label]',
    actionLabel: '[data-ps-action-label]'
  };

  const stateCache = new WeakMap();
  const resetTimers = new WeakMap();

  function getLocaleRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function setActionState(button, state, label) {
    if (!button) return;

    const previousTimer = resetTimers.get(button);
    if (previousTimer) {
      window.clearTimeout(previousTimer);
      resetTimers.delete(button);
    }

    button.dataset.state = state;

    if (typeof label === 'string') {
      const labelNode = button.querySelector(SELECTORS.actionLabel);
      if (labelNode) labelNode.textContent = label;
      button.setAttribute('aria-label', label);
    }
  }

  function scheduleActionReset(button, delay = 1700) {
    const timer = window.setTimeout(() => {
      button.disabled = false;
      setActionState(button, 'idle', button.dataset.mode === 'selector' ? 'Escolher opções' : 'Adicionar ao carrinho');
      resetTimers.delete(button);
    }, delay);

    resetTimers.set(button, timer);
  }

  function getDialogState(dialog) {
    if (stateCache.has(dialog)) return stateCache.get(dialog);

    const variantsNode = dialog.querySelector(SELECTORS.variants);
    let variants = [];

    try {
      variants = JSON.parse(variantsNode?.textContent || '[]');
    } catch (error) {
      console.error('[Pensualizei] Não foi possível ler as variantes do card.', error);
    }

    const groups = Array.from(dialog.querySelectorAll(SELECTORS.group));
    const selected = new Array(groups.length).fill(null);

    groups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      const defaultValue = group.dataset.defaultValue;

      if (defaultValue) {
        selected[index] = defaultValue;
      }
    });

    const state = { variants, groups, selected };
    stateCache.set(dialog, state);
    return state;
  }

  function matchesSelection(variant, selected) {
    return variant.options.every((optionValue, index) => {
      return selected[index] == null || selected[index] === optionValue;
    });
  }

  function findSelectedVariant(state) {
    if (state.selected.some((value) => value == null)) return null;

    return state.variants.find((variant) => {
      return variant.available && matchesSelection(variant, state.selected);
    }) || null;
  }

  function clearConflicts(state, protectedIndex) {
    state.selected.forEach((value, index) => {
      if (index === protectedIndex || value == null) return;

      const candidate = [...state.selected];
      const hasMatch = state.variants.some((variant) => {
        return variant.available && matchesSelection(variant, candidate);
      });

      if (!hasMatch) state.selected[index] = null;
    });
  }

  function renderVariantState(dialog) {
    const state = getDialogState(dialog);
    const input = dialog.querySelector(SELECTORS.variantInput);
    const submit = dialog.querySelector(SELECTORS.submit);
    const status = dialog.querySelector(SELECTORS.status);

    state.groups.forEach((group) => {
      const optionIndex = Number(group.dataset.optionIndex);
      const buttons = Array.from(group.querySelectorAll(SELECTORS.value));

      buttons.forEach((button) => {
        const candidate = [...state.selected];
        candidate[optionIndex] = button.dataset.value;

        const isPossible = state.variants.some((variant) => {
          return variant.available && matchesSelection(variant, candidate);
        });

        const isSelected = state.selected[optionIndex] === button.dataset.value;

        button.disabled = !isPossible;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
    });

    const variant = findSelectedVariant(state);

    if (variant) {
      input.value = String(variant.id);
      input.disabled = false;
      submit.disabled = false;
      status.textContent = `Pronto para adicionar — ${variant.price}`;
      dialog.dataset.selectedVariantId = String(variant.id);
      return;
    }

    input.value = '';
    input.disabled = true;
    submit.disabled = true;
    delete dialog.dataset.selectedVariantId;

    const missingNames = state.groups
      .filter((group) => {
        const index = Number(group.dataset.optionIndex);
        return !group.classList.contains('ps-variant-option--single') && state.selected[index] == null;
      })
      .map((group) => group.dataset.optionName);

    const partialMatch = state.variants.some((variant) => {
      return variant.available && matchesSelection(variant, state.selected);
    });

    if (!partialMatch && state.selected.some((value) => value != null)) {
      status.textContent = 'Essa combinação evaporou. Escolha outra opção.';
    } else if (missingNames.length) {
      status.textContent = `Escolha: ${missingNames.join(' e ')}.`;
    } else {
      status.textContent = 'Escolha as opções do produto.';
    }
  }

  function selectOption(button) {
    const dialog = button.closest(SELECTORS.dialog);
    if (!dialog) return;

    const group = button.closest(SELECTORS.group);
    const state = getDialogState(dialog);
    const optionIndex = Number(group.dataset.optionIndex);
    const currentValue = state.selected[optionIndex];
    const nextValue = button.dataset.value;

    state.selected[optionIndex] = currentValue === nextValue ? null : nextValue;

    if (state.selected[optionIndex] != null) {
      clearConflicts(state, optionIndex);
    }

    const error = dialog.querySelector(SELECTORS.error);
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    renderVariantState(dialog);
  }

  function openDialog(button) {
    const dialogId = button.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!dialog) return;

    renderVariantState(dialog);
    button.setAttribute('aria-expanded', 'true');
    setActionState(button, 'open', 'Fechar opções');

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    dialog.addEventListener('close', () => {
      button.setAttribute('aria-expanded', 'false');

      if (!['loading', 'success'].includes(button.dataset.state)) {
        setActionState(button, 'idle', 'Escolher opções');
      }
    }, { once: true });

    window.setTimeout(() => {
      dialog.querySelector(`${SELECTORS.value}:not(:disabled)`)?.focus();
    }, 30);
  }

  function closeDialog(dialog) {
    if (!dialog) return;

    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  function getCartSurface() {
    return document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer') ||
      document.querySelector('[data-cart-drawer]');
  }

  async function updateFallbackCartCount() {
    try {
      const response = await fetch(`${getLocaleRoot()}cart.js`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return;
      const cart = await response.json();
      const count = Number(cart.item_count || 0);

      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = String(count);
        node.setAttribute('data-cart-count', String(count));
      });

      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        const visibleCount =
          bubble.querySelector('span[aria-hidden="true"]') ||
          bubble.querySelector('span') ||
          bubble;

        visibleCount.textContent = String(count);
        bubble.classList.toggle('hidden', count === 0);
      });
    } catch (error) {
      console.warn('[Pensualizei] O contador do carrinho não pôde ser atualizado.', error);
    }
  }

  async function addVariantToCart(variantId, sourceButton) {
    const cartSurface = getCartSurface();
    const formData = new FormData();

    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    if (cartSurface && typeof cartSurface.getSectionsToRender === 'function') {
      const sections = cartSurface
        .getSectionsToRender()
        .map((section) => section.id)
        .filter(Boolean);

      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      if (typeof cartSurface.setActiveElement === 'function') {
        cartSurface.setActiveElement(sourceButton);
      }
    }

    const response = await fetch(`${getLocaleRoot()}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    let payload;

    try {
      payload = await response.json();
    } catch (error) {
      throw new Error('A loja não devolveu uma resposta válida.');
    }

    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || 'Não foi possível adicionar o produto.');
    }

    if (
      cartSurface &&
      typeof cartSurface.renderContents === 'function' &&
      payload.sections
    ) {
      cartSurface.renderContents(payload);
    } else {
      await updateFallbackCartCount();
    }

    document.dispatchEvent(new CustomEvent('pensualizei:cart-added', {
      bubbles: true,
      detail: {
        variantId: String(variantId),
        response: payload
      }
    }));

    return payload;
  }

  async function handleDirectAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.dataset.state === 'loading') return;

    button.disabled = true;
    setActionState(button, 'loading', 'Adicionando ao carrinho');

    try {
      await addVariantToCart(variantId, button);
      setActionState(button, 'success', 'Adicionado ao carrinho');
      scheduleActionReset(button);
    } catch (error) {
      console.error('[Pensualizei] Falha ao adicionar o produto.', error);
      setActionState(button, 'error', error.message);
      scheduleActionReset(button, 2200);
    }
  }

  async function handleVariantSubmit(form) {
    const dialog = form.closest(SELECTORS.dialog);
    const launcher = document.getElementById(form.dataset.launcherId);
    const input = form.querySelector(SELECTORS.variantInput);
    const submit = form.querySelector(SELECTORS.submit);
    const errorNode = form.querySelector(SELECTORS.error);

    if (!input?.value || submit.disabled) return;

    submit.disabled = true;
    submit.classList.add('is-loading');

    if (launcher) {
      launcher.disabled = true;
      setActionState(launcher, 'loading', 'Adicionando ao carrinho');
    }

    if (errorNode) {
      errorNode.hidden = true;
      errorNode.textContent = '';
    }

    try {
      await addVariantToCart(input.value, launcher || submit);

      if (launcher) {
        setActionState(launcher, 'success', 'Adicionado ao carrinho');
      }

      closeDialog(dialog);

      if (launcher) {
        scheduleActionReset(launcher);
      }
    } catch (error) {
      console.error('[Pensualizei] Falha ao adicionar a variante.', error);

      if (errorNode) {
        errorNode.textContent = error.message;
        errorNode.hidden = false;
      }

      if (launcher) {
        launcher.disabled = false;
        setActionState(launcher, 'error', error.message);
      }
    } finally {
      submit.classList.remove('is-loading');
      submit.disabled = !input.value;
    }
  }

  document.addEventListener('click', (event) => {
    const optionButton = event.target.closest(SELECTORS.value);
    if (optionButton) {
      event.preventDefault();
      selectOption(optionButton);
      return;
    }

    const closeButton = event.target.closest(SELECTORS.close);
    if (closeButton) {
      event.preventDefault();
      closeDialog(closeButton.closest(SELECTORS.dialog));
      return;
    }

    const actionButton = event.target.closest(SELECTORS.action);
    if (actionButton) {
      event.preventDefault();

      if (actionButton.disabled) return;

      if (actionButton.dataset.mode === 'direct') {
        handleDirectAdd(actionButton);
        return;
      }

      const dialogId = actionButton.getAttribute('aria-controls');
      const dialog = dialogId ? document.getElementById(dialogId) : null;

      if (dialog?.open) {
        closeDialog(dialog);
      } else {
        openDialog(actionButton);
      }

      return;
    }

    const dialogBackdrop = event.target.closest(SELECTORS.dialog);
    if (dialogBackdrop && event.target === dialogBackdrop) {
      closeDialog(dialogBackdrop);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(SELECTORS.form);
    if (!form) return;

    event.preventDefault();
    handleVariantSubmit(form);
  });
})();
